import {
  useState,
  useRef,
  forwardRef,
  useMemo,
  useCallback,
  MouseEventHandler,
} from 'react';
import Link from 'next/link';
import styled, { withTheme, DefaultTheme } from 'styled-components';
import { Input, AutoComplete, Button } from 'antd';
import { BaseOptionType } from 'antd/lib/select';
import type { BaseSelectRef } from 'rc-select';

import {
  SearchOption,
  getSearchOptionType,
} from '../../common/helpers/searchOption.utils';
import useI18n from '../../common/hooks/useI18n';
import { SearchFilter } from '../../common/hooks/useSearchService';
import { SearchOptionType } from '../../common/model/enums/searchOptionType.enum';

import SearchBarSuffix from './searchBarSuffix';

type Props = {
  height: string;
  value: string;
  options: SearchOption[];
  onValueChange: (value: string) => void;
  onSearch: (query: string) => void;
  onSelect: (option: SearchOption) => void;
  placeholder?: string;
  hasFilters?: boolean;
  backButton?: boolean;
  buttonBackgroundColor?: string;
  buttonColor?: string;
  inputPadding?: string;
  className?: string;
  onFiltersButtonClick?: () => void;
  applyFilter?: (filter: SearchFilter) => void;
  theme: DefaultTheme;
};

type SearchOptionWithType = SearchOption & { type: SearchOptionType };

const StyledAutoComplete = styled(AutoComplete)<{
  height: string;
  $buttonBackgroundColor: string;
  $buttonColor: string;
  $inputPadding: string;
}>`
  .ant-input-affix-wrapper {
    border-color: #d9d9d9;
    border-right-width: 0;
    &:focus,
    &:hover {
      border-color: #d9d9d9;
      border-right-width: 0;
      box-shadow: none;
    }
  }

  .ant-input-affix-wrapper-focused {
    border-right-width: 0;
    box-shadow: none;
  }

  .ant-input-group .ant-input-affix-wrapper:not(:last-child) {
    height: ${(props) => props.height};
    border-top-left-radius: calc(${(props) => props.height} / 2);
    border-bottom-left-radius: calc(${(props) => props.height} / 2);
    padding-left: 11px;
    padding-right: 11px;
    @media ${(props) => props.theme.breakpoints.xs} {
      padding-left: 8px;
      padding-right: 8px;
    }
  }

  .ant-input-affix-wrapper > input.ant-input {
    padding-left: ${(props) => props.$inputPadding};
    font-size: 16px;

    @media ${(props) => props.theme.breakpoints.xs} {
      font-size: 14px;
    }
  }

  .ant-input-group-addon:last-child {
    left: 0 !important;
    border-top-right-radius: calc(${(props) => props.height} / 2);
    border-bottom-right-radius: calc(${(props) => props.height} / 2);
  }

  .ant-input-search-button {
    height: ${(props) => props.height};
    background-color: white;
    padding: 8px;
    border-color: #d9d9d9;
    border-left-width: 0;
  }

  .ant-input-search
    > .ant-input-group
    > .ant-input-group-addon:last-child
    .ant-input-search-button {
    padding: 0 6px 0 0;
    border-top-right-radius: calc(${(props) => props.height} / 2);
    border-bottom-right-radius: calc(${(props) => props.height} / 2);
  }

  .anticon-search {
    background-color: ${(props) => props.$buttonBackgroundColor};
    color: ${(props) => props.$buttonColor};

    padding: 10px;
    height: 36px;
    width: 36px;
    border-radius: 18px;
  }
`;

const BackButton = styled.img`
  height: 12px;
  width: 12px;
  color: ${(props) => props.theme.colors.secondary};
`;

const computeOptionsByType = (
  searchOptions: SearchOptionWithType[],
  type: number
) => {
  return searchOptions
    .filter((option) => option.type === type)
    .map((option) => renderItem(option.text, option.count));
};

const renderTitle = (title: string) => {
  return <span>{title}</span>;
};

const renderItem = (title: string, count: number) => {
  return {
    value: title,
    label: (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        {title.trim()}
        <span>{count}</span>
      </div>
    ),
  };
};

const SearchBar = forwardRef<any, Props>(
  (
    {
      height,
      value,
      options: optionsFromProps,
      onValueChange,
      onSearch,
      onSelect,
      placeholder,
      hasFilters = false,
      backButton = false,
      buttonBackgroundColor,
      buttonColor = 'white',
      inputPadding = '14px',
      onFiltersButtonClick: onFiltersButtonClickFromProps,
      applyFilter,
      className,
      theme,
    },
    ref
  ) => {
    const i18n = useI18n();

    const [open, setOpen] = useState(false);

    let autoCompleteRef = useRef(null);
    if (ref) {
      autoCompleteRef = ref as React.MutableRefObject<BaseSelectRef>;
    }
    const filtersButtonRef: React.RefObject<HTMLButtonElement> = useRef(null);

    const options: SearchOptionWithType[] = useMemo(() => {
      return optionsFromProps
        .map((option) => ({
          ...option,
          type: getSearchOptionType(option),
        }))
        .filter((o) => o.count > 0);
    }, [optionsFromProps]);
    const autoCompleteOptions = useMemo(() => {
      const result: BaseOptionType[] = [];

      const countiesOptions = computeOptionsByType(
        options,
        SearchOptionType.COUNTY
      );
      const citiesOptions = computeOptionsByType(
        options,
        SearchOptionType.CITY
      );
      const zonesOptions = computeOptionsByType(options, SearchOptionType.ZONE);
      if (countiesOptions.length > 0) {
        result.push({
          label: renderTitle(i18n.t('search.searchBar.counties')),
          options: countiesOptions,
        });
      }
      if (citiesOptions.length > 0) {
        result.push({
          label: renderTitle(i18n.t('search.searchBar.cities')),
          options: citiesOptions,
        });
      }
      if (zonesOptions.length > 0) {
        result.push({
          label: renderTitle(i18n.t('search.searchBar.zones')),
          options: zonesOptions,
        });
      }

      return result;
    }, [i18n, options]);

    const onFiltersButtonClick: MouseEventHandler = useCallback(
      (e) => {
        e.stopPropagation();
        if (autoCompleteRef.current) {
          autoCompleteRef.current.blur();
        }
        if (filtersButtonRef.current) {
          filtersButtonRef.current.blur();
        }
        onFiltersButtonClickFromProps();
      },
      [onFiltersButtonClickFromProps]
    );

    return (
      <div className={className}>
        <StyledAutoComplete
          popupClassName="search-dropdown"
          $buttonBackgroundColor={buttonBackgroundColor || theme.colors.primary}
          $buttonColor={buttonColor}
          value={value}
          open={open}
          options={autoCompleteOptions}
          height={height}
          $inputPadding={inputPadding}
          style={{ width: '100%' }}
          ref={autoCompleteRef}
          onSearch={(newValue) => {
            setOpen(!!newValue);
            onValueChange(newValue);
          }}
          onSelect={(autoCompleteOption) => {
            const selectedOption = options.find(
              (o) => o.text === autoCompleteOption
            );
            if (selectedOption) {
              onSelect(selectedOption);
            }
            setOpen(false);
            setTimeout(() => {
              if (autoCompleteRef.current) {
                autoCompleteRef.current.blur();
              }
            });
          }}
          onFocus={() => {
            setOpen(!!value);
          }}
          onBlur={() => {
            setOpen(false);
          }}
        >
          <Input.Search
            size="large"
            placeholder={placeholder ?? i18n.t('search.searchBar.placeholder')}
            allowClear={true}
            prefix={
              backButton ? (
                <Link href="/" passHref>
                  <Button
                    type="text"
                    icon={
                      <BackButton
                        as="img"
                        className="anticon"
                        src={'/images/prev_no_circle.svg'}
                      />
                    }
                  />
                </Link>
              ) : null
            }
            suffix={
              onFiltersButtonClickFromProps && (
                <SearchBarSuffix
                  filtersButtonRef={filtersButtonRef}
                  hasFilters={hasFilters}
                  onFiltersButtonClick={onFiltersButtonClick}
                  applyFilter={applyFilter}
                />
              )
            }
            enterButton
            onSearch={(newValue) => {
              setOpen(false);
              onValueChange(newValue);
              onSearch(newValue);
              if (newValue) {
                setTimeout(() => {
                  if (autoCompleteRef.current) {
                    autoCompleteRef.current.blur();
                  }
                });
              }
            }}
          />
        </StyledAutoComplete>
      </div>
    );
  }
);

SearchBar.displayName = 'SearchBar';

export default withTheme(SearchBar);
